import { Component, OnInit, Input, ViewEncapsulation, HostListener } from '@angular/core';
import { Location } from '@angular/common';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { URLParams } from '../interfaces/url-params.interface';
import { CookieService } from 'ngx-cookie-service';
import { MatDialog } from '@angular/material/dialog';
import { LeavingGateModalComponent } from './leaving-gate-modal/leaving-gate-modal.component';
import { NgRedux } from '@ptl/angular-redux-store';
import { Subscription } from 'rxjs';
import { StateStructure } from '@ptl/state';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ContentStorageService, PtlWindowLocationService } from '@ptl/global-components';
import { SHOPPING_DATE_STATE_NAME, setNextYearShoppingDates } from 'src/app/angular-elements/shared/components/configs/reducers/shopping-dates.reducer';
import { AuthoringModeService } from 'src/app/shared/services/authoring-mode.service';
import { environment } from 'src/environments/environment';
import { CommonLoginService } from '../../services/common-login.service';
import { BrandAwareService } from '../../../demographics/zip-county-modal/brand-aware/brand-aware.service';
import { ZipCountyModalComponent } from '../../../demographics/zip-county-modal/zip-county-modal.component';
import { Brand, BrandingConfigurationService } from '@ptl/brand';
import { DateService } from '../../../../shared/services/date.service';
import { EffectiveDateStateStructure } from '../configs/interfaces/effective-date.state-structure.interface';
import { setDirtyEffectiveDate, setEffectiveDate } from '../configs/reducers/effective-date.reducer';
import { plansNeedRefreshedFromBackend } from '../../../plan-selection/configs/reducers/plans.reducer';
import moment from 'moment';
import { AGENCY_CONTENT_SERVICE_KEY, AgencyContentMap, LINE_OF_BUSINESS_ENUM, LOGIN_STRATEGY } from '@ptl/account-management';
import { setLob } from '../configs/reducers/lob.reducer';
import { REFERRAL_QUERY_PARAM_KEYS, GLOBAL_DATA_PARAM_KEYS } from '../../../../shared/enums/query-params.enum';
import { ShoppingDateStateStructure } from '../configs/interfaces/shopping-date.state-structure.interface';
import { DOCTOR_VENDOR_TYPE } from 'src/app/angular-elements/medical-information/doctor-information/configs/enums/doctor-vendor-type.enum';
import { setProviderVendorType } from 'src/app/angular-elements/medical-information/doctor-information/configs/reducers/doctor-information.reducer';
import { PrePopulatedDataStateStructure } from '../configs/interfaces/prePopulatedDataStateStructure.interface';
import { GlobalDataStateStructure, URL_PARAM_REFERRAL_TYPE } from '../configs/interfaces/globalStateStructure.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { saveZipCounty } from '../../../demographics/zip-county-modal/reducers/zipCounty.reducer';
import { saveReferralType } from '../configs/reducers/globalData.reducer';

export interface BrandMeOverride {
  brandMeRegion: string;
  brandMeOriginalPath: string;
  brandMeOverrideUrl: string;
}

@Component({
    selector: 'app-global-hidden',
    templateUrl: './global-hidden.component.html',
    styleUrls: ['./global-hidden.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class GlobalHiddenComponent implements OnInit {

  hasbeenCalled: boolean = false;
  isAuthoring = false;
  urlQueryParams: URLParams;
  prePopulateData: PrePopulatedDataStateStructure;
  globalData: GlobalDataStateStructure;

  // Branding and Leaving Gate Configs
  @Input() noBrandy: any;
  @Input() hostSecureString: any;
  @Input() brandMeOverrides: string;

  // Producer login configurations
  @Input() postLoginPages: string;
  @Input() loginStrategy: LOGIN_STRATEGY = LOGIN_STRATEGY.CREDENTIALSV2;
  @Input() sessionTimeInMinutes: string = "60";

  // Next Year Shopping Dates Config
  @Input() sellThisYearProductsDate: string; // Override to show year selection up to this date, ex: wholecare can sell 2023 plans up to end of December
  @Input() sellPlansStartDate: string; // Need to hook this up to State structure and affect enroll button based on it.
  @Input() showPlansStartDate: string; // basically window shopping

  // State LOB configuration
  @Input() lob: LINE_OF_BUSINESS_ENUM;

  @Input() vendorType: DOCTOR_VENDOR_TYPE = DOCTOR_VENDOR_TYPE.KYRUUS;

  constructor(
    private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService,
    private location: Location,
    private commonUtilityService: CommonUtilityService,
    private cookies: CookieService,
    private matDialog: MatDialog,
    private brandAware: BrandAwareService,
    private windowLocationService: PtlWindowLocationService,
    private authoringMode: AuthoringModeService,
    private commonLogin: CommonLoginService,
    private brandConfigService: BrandingConfigurationService,
    private contentService: ContentStorageService,
    private dateService: DateService,
    private snackBar: MatSnackBar
  ) {
  }

  async ngOnInit() {
    this.isAuthoring = this.authoringMode.getAuthoringMode();
    this.setLineOfBusiness();
    this.setProviderVendorType();
    this.prePopulateData = this.reducerUtilityService.getComponentStates().PrePopulateData;
    this.globalData = this.reducerUtilityService.getState('GlobalData');


    // Order matters here. Must initialize shopping dates before checking effective date
    this.initializeShoppingDates();
    if (this.lob !== LINE_OF_BUSINESS_ENUM.INDIVIDUAL) {
      this.updateEffectiveDateYearForShoppingDates();
    }

    // Security check on every page
    this.commonLogin.commonLoginCheck(this.postLoginPages, this.loginStrategy, +this.sessionTimeInMinutes);

    // Save query params and update them if the url changes
    await this.initializeAndProcessQueryParams();
    this.location.subscribe(x => {
      this.saveQueryParams();
    });

    // Check branding requirements for current page
    this.brandIfNotBrandy();
  }

  /** @description default the shopping dates to the correct day and year if they are not configured in AEM */
  private initializeShoppingDates() {
    // Format should be YYYY-MM-DD
    let currentYear = this.dateService.getTodayAsMoment().year();
    const defaultSellThisYearDate = currentYear + '-11-30';
    const defaultShowPlansDate = currentYear + '-10-01';
    const defaultSellPlansDate = currentYear + '-10-15';
    
    this.ngRedux.dispatch(setNextYearShoppingDates({
      sellThisYearProductsDate: this.sellThisYearProductsDate ? this.sellThisYearProductsDate : defaultSellThisYearDate,
      showNextYearProductsDate: this.showPlansStartDate ? this.showPlansStartDate : defaultShowPlansDate,
      sellNextYearProductsDate: this.sellPlansStartDate ? this.sellPlansStartDate : defaultSellPlansDate
    }));
  }

  private async initializeAndProcessQueryParams() {
    if (!this.isAuthoring && !this.detectBot()) {
      this.saveQueryParams();

      let url: URL = new URL(window.location.href);
      let params: URLSearchParams = url.searchParams;

      // Check for a redirect to a document as a query param
      let documentLink = params.get(REFERRAL_QUERY_PARAM_KEYS.DOCUMENT_LINK);
      if (documentLink) {
        this.windowLocationService.goToUrl(documentLink);
      }

      // Check for zip/county being passed for auto-branding
      let zipCode = params.get(GLOBAL_DATA_PARAM_KEYS.ZIP_CODE);
      let county = params.get(GLOBAL_DATA_PARAM_KEYS.COUNTY);
      if (zipCode && county) {
        // if we succesfully updated the zip and county, we want to remove the query params
        // and refresh the page to make sure all components are brand aligned
        let response = await this.commonUtilityService.setBrandByZipCode(zipCode, county);
        if (response) {
          this.windowLocationService.goToUrl(window.location.pathname);
        }
      }

      // Check for referral ID and update the status
      let referralId = params.get(REFERRAL_QUERY_PARAM_KEYS.REFERRAL_ID_PARAM);
      if (referralId) {
        const globalDataState: GlobalDataStateStructure = this.reducerUtilityService.getComponentStates().GlobalData;
        const referralType = globalDataState.referralType;
        if ( referralType !== URL_PARAM_REFERRAL_TYPE.SAVED_QUOTE && referralType !== URL_PARAM_REFERRAL_TYPE.PERSONAL_PRODUCER_URL){
          this.ngRedux.dispatch(saveReferralType(URL_PARAM_REFERRAL_TYPE.REFERRAL));
        }
        // We don't want the whole component to fail if this service fails for some reason
        try {
          await this.commonUtilityService.registerReferralClick(referralId);
        }
        catch(e) {
          console.warn(`Failed to update Referral ID ${referralId} in status service`, e);
        }
      }
    }
  }

  private updateEffectiveDateYearForShoppingDates() {
    const shoppingDatesState: ShoppingDateStateStructure = this.reducerUtilityService.getApplicationStates()[SHOPPING_DATE_STATE_NAME];
    const effectiveState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');
    const today = this.dateService.getTodayAsMoment();
    const nextMonth = this.dateService.getFirstOfNextMonth();

    // We need to reset the effective date if it is set to something we cannot use:
    // 1. Check the shopping dates to see if we need to allow effective dates later than december
    // 2. Any time in december we need to bump the effective date to next year
    if (today.isAfter(moment(shoppingDatesState.sellThisYearProductsDate)) &&
       (today.year() < nextMonth.year() && effectiveState.effectiveDateYear === today.year().toString())) {
      this.ngRedux.dispatch(setDirtyEffectiveDate());
      this.ngRedux.dispatch(setEffectiveDate(nextMonth.format()));
      this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
    }
  }

  setLineOfBusiness() {
    if (this.lob) {
      // TODO - LOB storage in NG Redux is deprecated, need to subscribe to the Content Storage map instead
      this.ngRedux.dispatch(setLob(this.lob));
      
      // TODO - content storage map is deprecated, use the NG Redux Value
      this.contentService.storeContentMapFromComponent(AGENCY_CONTENT_SERVICE_KEY, {lob: this.lob} as AgencyContentMap);
    }
  }

  async saveQueryParams(): Promise<any> {
    const urlString = this.location.path(true);
    const qParamStr = urlString.slice(urlString.indexOf('?') + 1);
    const qParamArr = qParamStr.split('&');
    const qParamObj = {};
    for (const str of qParamArr) {
      const pair = str.split('=');
      qParamObj[pair[0]] = pair[1];
    }


    /** NPN and AgencyID Constraints */
    let agentInfoMinLength = 5;
    let agentInfoMaxLength = 12;
    const specialChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
    const whitespace = /\s+/g;

    let agentCode = qParamObj['agentCode'];
    let agencyNumber = qParamObj['agencyNumber'];

    if (agentCode) {
      if (agentCode.length > agentInfoMaxLength
        || agentCode.length < agentInfoMinLength
        || specialChars.test(agentCode)
        || whitespace.test(agentCode)
      ) {
        agentCode = "77777777";
      }
    }

    if (agencyNumber) {
      if (agencyNumber.length > agentInfoMaxLength
        || agencyNumber.length < agentInfoMinLength
        || specialChars.test(agencyNumber)
        || whitespace.test(agencyNumber)
      ) {
        agencyNumber = "1111111";
      }
    }

    const urlParams: URLParams = {}

    if (agentCode) { urlParams.agentCode = agentCode; }
    if (agencyNumber) { urlParams.agencyNumber = agencyNumber; }
    if (qParamObj[REFERRAL_QUERY_PARAM_KEYS.CUSTOMER_ID_PARAM]) { urlParams.customerId = qParamObj[REFERRAL_QUERY_PARAM_KEYS.CUSTOMER_ID_PARAM]; }
    if (qParamObj[REFERRAL_QUERY_PARAM_KEYS.OPPORTUNITY_ID_PARAM]) { urlParams.opportunityId = qParamObj[REFERRAL_QUERY_PARAM_KEYS.OPPORTUNITY_ID_PARAM]; }
    if (qParamObj[REFERRAL_QUERY_PARAM_KEYS.CRM_OPPORTUNITY_ID_PARAM]) { urlParams.crmOpportunityId = qParamObj[REFERRAL_QUERY_PARAM_KEYS.CRM_OPPORTUNITY_ID_PARAM]; }
    if (qParamObj[REFERRAL_QUERY_PARAM_KEYS.REFERRAL_ID_PARAM]) { urlParams.referralid = qParamObj[REFERRAL_QUERY_PARAM_KEYS.REFERRAL_ID_PARAM]; }
    if (qParamObj['P1']) { urlParams.P1 = qParamObj['P1']; }
    if (qParamObj['P2']) { urlParams.P2 = qParamObj['P2']; }
    if (qParamObj['P3']) { urlParams.P3 = qParamObj['P3']; }
    if (qParamObj['P4']) { urlParams.P4 = qParamObj['P4']; }
    if (qParamObj['P5']) { urlParams.P5 = qParamObj['P5']; }
    if (qParamObj['P6']) { urlParams.P6 = qParamObj['P6']; }
    if (qParamObj['P7']) { urlParams.P7 = qParamObj['P7']; }
    if (qParamObj['token']) { urlParams.linkShareToken = qParamObj['token']; }

    this.urlQueryParams = urlParams;
    if (Object.keys(urlParams).length > 0) {
      this.commonUtilityService.saveQueryParams(urlParams);
    }

    if (
      urlParams['P1'] ||
      urlParams['P2'] ||
      urlParams['P3'] ||
      urlParams['P4'] ||
      urlParams['P5'] ||
      urlParams['P6'] ||
      urlParams['P7']
    ) {
      this.windowLocationService.goToUrl('/beta/login');
    }
  }

  brandIfNotBrandy() {
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    const youreAFineGirl = this.cookies.get('brand');

    if (!this.isAuthoring && (!youreAFineGirl || !zipCountyState.zipCode)) {
      if (!this.commonUtilityService.pageMatched(this.noBrandy)) {
        this.matDialog.open(ZipCountyModalComponent, {
          maxWidth: '650px',
          maxHeight: '100vh',
          disableClose: true,
          data: {
            forceBranding: true,
            yearSelectionConfig: {
              hideYearSelection: true,
            },
            modalContent: {
              heading: 'Information Needed!',
              contentMain: 'Enter your ZIP code and select the county in which you reside to see the most relevant information for you!',
              buttonLabel: 'Continue'
            }
          }
        });
      }

    }
  }

  @HostListener('document:click', ['$event', '$event.target'])
  ifLeavingSite(event: MouseEvent, targetElement) {

    let href = '';
    if (targetElement) {
      href = targetElement?.href || targetElement.parentElement?.href;
    }
    // Should this iterate through the parents to bubble up to find any hrefs?
    if (/^http.:\/\/.*/.test(href)) {
      // URLS starting with brandme/ (and only that, not brandme.com!) will get branded before continuing.
      if (href.match('brandme/')) {
        this.linkToBrandMePage(event, href);
      }
      // SAFE - Use character class without nested quantifiers
      else if (this.hostSecureString && !href.match(`^https:\/\/[a-zA-Z0-9.\-]*${this.hostSecureString}[a-zA-Z0-9.\-]*`)) {
        event.preventDefault();

        this.matDialog.open(LeavingGateModalComponent, {
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          data: { href }
        });
      }
    }
    
    return null;
  }

  detectBot() {
    const botString = /googlebot\/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis/;
    return botString.test(navigator.userAgent);
  }

  // CAUTION: Using the brandme feature will automatically brand the user after they enter the zip code
  // This should be avoided in the producer portal as it can brand users to a region they do not have agencies for
  linkToBrandMePage(event: MouseEvent, href: string) {
    event.preventDefault();
    const currentBrand = this.brandConfigService.getCurrentBrand();

    if (currentBrand?.cookieBrandCode) {
      // If brand exists, then don't open modal, but still apply brand overrides
      this.goToBrandedUrl(currentBrand, href);
    } 
    else {
      // Open a Zip County Modal to Brand the url before continuing.
      this.matDialog.open(ZipCountyModalComponent, {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        data: {
          outboundUrl: href,
          forceBranding: false,
          yearSelectionConfig: {
            hideYearSelection: true,
          },
          modalContent: {
            heading: 'Information Needed!',
            contentMain: 'We need to know what county you are in to get you to the correct place!',
            buttonLabel: 'Continue'
          }
        }
      })
      .afterClosed().subscribe((isCancelled) => {
        const newBrand = this.brandConfigService.getCurrentBrand();

        if (newBrand?.cookieBrandCode && !isCancelled) {
          this.goToBrandedUrl(newBrand, href);
        }
      });
    }
  }

  goToBrandedUrl(brand: Brand, href: string) {
    let modifiedHref = this.applyBrandMeOverrides(brand, href);
    
    // If modifiedHref does not have a path symbol, it is assumed to be a message for an invalid zip for this brandme URL
    if (!modifiedHref.includes("/")){
      this.snackBar.open(modifiedHref, 'OK', {
        duration: 5000,
        panelClass: 'positive-panel-class'
      });

      // clear the brand in this case
      this.ngRedux.dispatch(saveZipCounty('',null));
      this.brandConfigService.clearBrand();
      return;
    }

    this.brandAware.goToBrandedUrl(modifiedHref);
  }

  applyBrandMeOverrides(brand: Brand, href: string) {
    if (!this.brandMeOverrides) {
      return href;
    }

    let brandConfig:BrandMeOverride[] = JSON.parse(this.brandMeOverrides);
    brandConfig.forEach((brandMe) => {
      if (href.indexOf(brandMe.brandMeOriginalPath) !== -1 && brandMe?.brandMeRegion === brand.region) {
        href = brandMe.brandMeOverrideUrl;
      }
    });

    return href;
  }

  setProviderVendorType() {
    this.ngRedux.dispatch(setProviderVendorType(this.vendorType));
  }

}
